import React from 'react';
import {cn} from '@rocket-ui/tools';
import OffersFilterIcon from 'shared/assets/svg/offers-filter.svg?react';
import {OFFERS_SORT_OPTIONS, isSameOffersSort, type OffersSort} from '../../model/offersFilters';
import styles from './OffersToolbar.module.css';

interface OffersToolbarProps {
	sort: OffersSort;
	onSortChange: (sort: OffersSort) => void;
	onFilterClick: () => void;
}

export function OffersToolbar({sort, onSortChange, onFilterClick}: OffersToolbarProps): React.ReactElement {
	const [open, setOpen] = React.useState(false);
	const rootRef = React.useRef<HTMLDivElement | null>(null);
	const currentSort =
		OFFERS_SORT_OPTIONS.find((option) => isSameOffersSort(option.id, sort)) ?? OFFERS_SORT_OPTIONS[0]!;

	React.useEffect(() => {
		if (!open) return;

		const handlePointerDown = (event: MouseEvent | PointerEvent) => {
			const root = rootRef.current;
			if (!root) return;
			if (event.target instanceof Node && root.contains(event.target)) return;
			setOpen(false);
		};

		const handleKeyDown = (event: KeyboardEvent) => {
			if (event.key === 'Escape') setOpen(false);
		};

		window.addEventListener('pointerdown', handlePointerDown);
		window.addEventListener('keydown', handleKeyDown);
		return () => {
			window.removeEventListener('pointerdown', handlePointerDown);
			window.removeEventListener('keydown', handleKeyDown);
		};
	}, [open]);

	return (
		<div className={styles.toolbar}>
			<button type="button" className={styles.filterButton} onClick={onFilterClick} aria-label="Фильтры">
				<OffersFilterIcon aria-hidden />
			</button>

			<div ref={rootRef} className={styles.sortWrap}>
				<button
					type="button"
					className={styles.sortButton}
					aria-haspopup="menu"
					aria-expanded={open}
					onClick={() => setOpen((value) => !value)}
				>
					<span>{currentSort.label}</span>
					<span className={styles.sortCaret} aria-hidden>
						▼
					</span>
				</button>

				{open ? (
					<div role="menu" className={styles.sortMenu}>
						{OFFERS_SORT_OPTIONS.map((option) => (
							<button
								key={`${option.id.by}:${option.id.direction}`}
								type="button"
								role="menuitem"
								className={cn(styles.sortItem, isSameOffersSort(option.id, sort) && styles.sortItemActive)}
								onClick={() => {
									onSortChange(option.id);
									setOpen(false);
								}}
							>
								{option.label}
							</button>
						))}
					</div>
				) : null}
			</div>
		</div>
	);
}
